import React from "react";
import { Grid, Typography } from "@mui/material";

import CardDefault from "@components/CardDefault";
import { CheckListIcon } from "@components/Icons";
import ProgressBar from "@components/ProgressBar";
import { SelectField } from "@components/TextFieldSelect/SelectField";
import { SelectOption } from "@components/TextFieldSelect/interface";

import { deliveryOptions } from "@utils/DeliveryType";
import {
  capitalizeFirstLetterFullString as upperCase,
  capitalizeWords,
} from "@utils/index";
import {
  addressToSelectOptions,
  companyToSelectOptions,
  basisToSelectOptions,
} from "@components/TextFieldSelect/helpers";

import { useFormQuery } from "../hooks/useFormQuery";
import { SelectCNPJ } from "@components/TextFieldSelect/SelectCNPJ";
import { maskCNPJAlfa } from "@utils/cnpj";

const formatCenter = (value: unknown, options: SelectOption[]) => {
  const label = options.find((o) => o.value === value)?.label ?? "";
  return label.slice(0, 4).toUpperCase() + upperCase(label.slice(4));
};

const formatFreight = (value: unknown, options: SelectOption[]) =>
  capitalizeWords(options.find((o) => o.value === value)?.label ?? "");

const formatAddress = (value: unknown, options: SelectOption[]) =>
  upperCase(options.find((o) => o.value === value)?.label ?? "");

const FormQuery: React.FC = () => {
  const {
    values,
    setFieldValue,
    fieldErrorProps,
    cnpjOptions,
    dataCustomer,
    branchesWithdrawBasis,
    isLoading,
    CNPJDetailsLoad,
    progress,
    handleCNPJChange,
    handleAddressChange,
    handleWithdrawBasisChange,
  } = useFormQuery();

  const formatCnpjLabel = (value: unknown, options: SelectOption[]) => {
    const option = options.find((o) => o.value === value);
    return `${upperCase(option?.label ?? "")} | ${
      maskCNPJAlfa(option?.value) ?? ""
    }`;
  };

  const renderCnpjMenuItem = (option: SelectOption) => (
    <>
      <Typography variant="body1">{upperCase(option.label)}</Typography>
      <Typography variant="body2">{maskCNPJAlfa(option.value)}</Typography>
    </>
  );

  return (
    <CardDefault>
      {CNPJDetailsLoad && (
        <ProgressBar bgcolor="#D91F05" progress={progress} height={4} />
      )}

      <Grid container alignItems="center" sx={{ px: 3, pt: 2, pb: 1.5 }}>
        <Grid item xs={10}>
          <Typography variant="h5" sx={{ color: "#3E3D3D", fontWeight: 700 }}>
            Consultar preços de produtos
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CheckListIcon fontSize="small" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ px: 3, pb: 3 }}>
        <Grid item xs={12} sm={6} md={4.5}>
          <SelectCNPJ
            name="CNPJ"
            label="Razão social"
            value={values.CNPJ}
            options={companyToSelectOptions(cnpjOptions)}
            onChange={(e) => handleCNPJChange(e.target.value)}
            formatLabel={formatCnpjLabel}
            renderMenuItem={renderCnpjMenuItem}
            {...fieldErrorProps("CNPJ")}
            loading={isLoading}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4.5}>
          <SelectField
            name="address"
            label="Endereço"
            value={values.address?.id ?? ""}
            options={addressToSelectOptions(dataCustomer?.address)}
            onChange={(e) => handleAddressChange(e.target.value)}
            formatLabel={formatAddress}
            {...fieldErrorProps("address")}
            loading={isLoading}
            fullWidth
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <SelectField
            name="withdrawBasisID"
            label="Centro"
            value={values.withdrawBasisID}
            options={basisToSelectOptions(branchesWithdrawBasis)}
            onChange={(e) =>
              handleWithdrawBasisChange(e.target.value as string)
            }
            formatLabel={formatCenter}
            {...fieldErrorProps("withdrawBasisID")}
            loading={isLoading}
            fullWidth
          />
        </Grid>

        <Grid item xs={6} sm={2} md={1}>
          <SelectField
            name="freightType"
            label="Frete"
            options={deliveryOptions}
            value={values.freightType}
            onChange={(e) => setFieldValue("freightType", e.target.value)}
            formatLabel={formatFreight}
            {...fieldErrorProps("freightType")}
            loading={isLoading}
            fullWidth
          />
        </Grid>
      </Grid>
    </CardDefault>
  );
};

export { FormQuery };
